import { decrypt } from 'qp-utility';
import { BaseRequestModel } from '../services/evergent/base/base.request.model';
import { BaseResponseModel } from '../services/evergent/base/base.response.model';
import { AppConfig } from '../utils/constants/app-config.constant';

import { HttpHeaders } from '@angular/common/http';
import { RegionAPIResult } from '../services/geolocation.service';
import { LocalStorageService } from '../services/storage/local-storage.service';
import { KEYS } from './constants/storage.constants';

const KEY_AUTHORIZATION = 'Authorization';
const KEY_CONTENT_TYPE = 'Content-Type';

/**
 * Retruns object new HttpHeaders instance
 * Includes key-authorization and content type headers
 * Used by entitlement service
 *
 * @param localStorage
 * @returns
 */
export const getAuthHeaders = (localStorage: LocalStorageService) => {
  const loginAccessToken = localStorage.getItem('accessToken') || '';
  const headers = new HttpHeaders({
    [KEY_CONTENT_TYPE]: 'application/json',
    [KEY_AUTHORIZATION]: `Bearer ${loginAccessToken}`,
  });
  return {
    headers: headers,
  };
};

/**
 * Retruns object new HttpHeaders instance
 * Includes key-authorization and content type headers
 * Used by entitlement service
 *
 * @param localStorage
 * @returns
 */
export const getHeaders = () => {
  const headers = new HttpHeaders({
    [KEY_CONTENT_TYPE]: 'application/json',
  });
  return {
    headers: headers,
  };
};

export const isSuccessfulResponse = (response: BaseResponseModel): boolean => {
  return !!(response && response.responseCode && response.responseCode === '1');
};

export const getBaseRequestData = (appConfig: AppConfig): BaseRequestModel => ({
  channelPartnerID: getChannelPartnerID(appConfig) ?? undefined,
  apiUser: appConfig.umsApiUser,
  apiPassword: decrypt(appConfig.umsApiToken),
});

export const getBaseRequestGlobalData = (
  appConfig: AppConfig
): BaseRequestModel => ({
  channelPartnerID: appConfig.evGlobalBU,
  apiUser: appConfig.umsApiUser,
  apiPassword: decrypt(appConfig.umsApiToken),
});

export const getErrorMessage = (responseBase: BaseResponseModel): string => {
  return (
    (responseBase &&
      responseBase.failureMessage &&
      responseBase.failureMessage.reduce(
        (_finalMsg, failure) => `${failure.errorMessage} `,
        ''
      )) ||
    ''
  );
};

export const handleAccountResponse = (accountServiceResponse: any): void => {
  try {
    const accountProfileResponse =
      accountServiceResponse?.GetAccountProfileResponseMessage;
    sessionStorage.setItem('refID', accountProfileResponse?.accountId);
    if (accountProfileResponse && accountProfileResponse.prevSubExpDateTime) {
      setSubscriptionDetails(accountProfileResponse);
    }
    // getting subscription expiration time to compare days left
    setExpiry(accountProfileResponse);
  } catch (e) {
    console.warn('error on account response');
  }
};

const setSubscriptionDetails = (accountProfileResponse: any) => {
  try {
    sessionStorage.setItem(
      'prevSubExpDateTime',
      accountProfileResponse.prevSubExpDateTime
    );
    const isSubCancelled = isSubscriptionCancelled(accountProfileResponse);
    // setting hasSubCancelled
    if (isSubCancelled) {
      const attributeTypeItem = Object.assign({}, ...isSubCancelled);
      const attributeTypeName = attributeTypeItem.attributeType.attributeName;
      if (attributeTypeName === 'hasSubCancelled') {
        sessionStorage.setItem('hasSubCancelled', attributeTypeItem.value);
      }
    }
  } catch {}
};

const isSubscriptionCancelled = (accountProfileResponse: any) => {
  return accountProfileResponse.stringAttribute.filter((item: any) => {
    const check = item.attributeType.hasOwnProperty('attributeName');
    const att = item.attributeType;
    const attributeStatus = att as unknown as { attributeName: string };
    const attributeType = attributeStatus.attributeName;

    return check && attributeType === 'hasSubCancelled';
  });
};

const setExpiry = (accountProfileResponse: any) => {
  if (accountProfileResponse?.nextBillingDateTime === 0) {
    sessionStorage.setItem('subwillexpire', 'true');
    setExpiryDate(accountProfileResponse);
    return true;
  }

  sessionStorage.setItem('subwillexpire', 'false');
  return false;
};

const setExpiryDate = (accountProfileResponse: any) => {
  if (accountProfileResponse.subscriptionExpDateTime < 0) {
    sessionStorage.setItem('expireDate', String(0));
  } else {
    sessionStorage.setItem(
      'expireDate',
      String(accountProfileResponse.subscriptionExpDateTime)
    );
  }
};

export const errorCode = (payload: any | undefined) => {
  if (
    payload &&
    payload.failureMessage &&
    payload.failureMessage.length &&
    payload.failureMessage.length > 0
  ) {
    return payload.failureMessage[0].errorCode;
  }
  return undefined;
};

/*
 * Get channelParnerID in config
 *
 * @param applicationConfig
 * @param localStorage
 * @returns
 */
export const getChannelPartnerID = (applicationConfig: AppConfig) => {
  /* if (sessionStorage.getItem(KEYS.ACCOUNT_CHANNEL_PARTNER_ID)) {
    // return user channel partner ID
    return sessionStorage.getItem(KEYS.ACCOUNT_CHANNEL_PARTNER_ID);
  } */
  // return config channel partner ID
  return applicationConfig.channelPartnerID;
};

/**
 * Validate channelParnerID by edgeworker location
 *
 * @param applicationConfig
 * @param localStorage
 * @returns
 */
export const getChannelPartnerIDByLocation = (applicationConfig: AppConfig) => {
  const location = JSON.parse(localStorage.getItem(KEYS.LOCATION) || '{}');
  return getChannel(location, applicationConfig.evBUChannelList);
};

/**
 * Get channel based on country code
 *
 * @param countryCode
 * @returns
 */
export const getChannel = (
  location: RegionAPIResult,
  evBUChannelList: any
): string => {
  const channel = JSON.parse(atob(evBUChannelList));
  const channelData = channel.filter(
    (element: any) =>
      element.group.toLowerCase() === location.group.toLowerCase()
  );
  return channelData && channelData.length > 0
    ? channelData[0].evChannelName
    : 'AHA_ROW';
};
